import { chevronDownOutline, chevronUpOutline } from 'ionicons/icons'
import { useCallback, useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { Option } from '~/shared/config/constants'
import { Button } from '~/shared/ui/Buttons'
import { DrawerComponent, useDrawer } from '~/shared/ui/Drawer'
import { VisibleInput } from '../Select/styled'
import { Checkbox } from './Checkbox/Checkbox'
import {
  Title,
  DrawerWrapper,
  InputWrapper,
  InputIcon,
  ButtonsWrapper,
  CheckBoxWrapper,
} from './styled'

type SelectMultipleProps<T extends Option> = {
  field: string
  options: T[]
  label: string
  modalTitle: string
  name: string
  onChange?: () => void
}

export function SelectMultiple<T extends Option>({
  field,
  options,
  label,
  modalTitle,
  onChange,
  name,
}: SelectMultipleProps<T>) {
  const [isShow, openDrawer, closeDrawer] = useDrawer()
  const { setValue, watch } = useFormContext()
  const valuesForm: T[] = watch(field) || []
  const [selectedOptions, setSelectedOptions] = useState<T[]>(valuesForm || [])

  const handleDrawerClose = () => {
    closeDrawer()
  }

  const handleSelectedValueCheck = useCallback(
    (option: T) =>
      selectedOptions.some((selected) => selected.id === option.id),
    [selectedOptions],
  )

  const handleOnChange = useCallback(
    (option: T) => {
      if (handleSelectedValueCheck(option)) {
        setSelectedOptions((prevOptions) =>
          prevOptions.filter((value) => value.id !== option.id),
        )
      } else {
        setSelectedOptions((prevOptions) => [...prevOptions, option])
      }
    },
    [handleSelectedValueCheck],
  )

  const handleOnClear = () => {
    setSelectedOptions([])
    setValue(field, undefined, { shouldValidate: true })
    handleDrawerClose()
    onChange?.()
  }

  const handleOnApply = () => {
    setValue(field, selectedOptions, { shouldValidate: true })
    handleDrawerClose()
    onChange?.()
  }

  useEffect(() => {
    setSelectedOptions(valuesForm)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShow])

  const renderCheckbox = useCallback(
    (option: T) => (
      <Checkbox
        key={option.id}
        label={option.label}
        onChange={() => handleOnChange(option)}
        checked={handleSelectedValueCheck(option)}
      />
    ),
    [handleOnChange, handleSelectedValueCheck],
  )

  return (
    <Controller
      name={field}
      render={({ fieldState: { error, invalid } }) => {
        return (
          <>
            <InputWrapper onClick={openDrawer}>
              <VisibleInput
                name={name}
                className={invalid ? 'ion-invalid ion-touched' : ''}
                value={valuesForm.map(({ label }) => label).join(', ')}
                label={label}
                mode='md'
                readonly
                labelPlacement='floating'
                fill='outline'
                errorText={error?.message || 'Обязательное поле'}
              />
              <InputIcon
                icon={isShow ? chevronUpOutline : chevronDownOutline}
              />
            </InputWrapper>

            <DrawerComponent
              isShow={isShow}
              onCancel={handleDrawerClose}
              contentProps={{
                className: 'ion-content-scroll-host',
                style: { height: '90vh' },
              }}
              data-testId={`info-source-picker-${name}`}
            >
              <DrawerWrapper>
                <Title>{modalTitle}</Title>

                <CheckBoxWrapper>
                  {options.map((option) => renderCheckbox(option))}
                </CheckBoxWrapper>

                <ButtonsWrapper>
                  <Button
                    fullWidth
                    variant='lightSteelBlueBorder'
                    onClick={handleOnClear}
                  >
                    Сбросить
                  </Button>
                  <Button
                    fullWidth
                    variant='limeFlooded'
                    onClick={handleOnApply}
                  >
                    Применить
                  </Button>
                </ButtonsWrapper>
              </DrawerWrapper>
            </DrawerComponent>
          </>
        )
      }}
    />
  )
}
